import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CredentialCard, CredentialRow } from "@/shared/ui/ui";
import { Check, Copy, KeyRound, Landmark, MapPin, UserRound } from "lucide-react";
import {
  AUTH_ROUTES,
  changePasswordRequestSchema,
  COUNTRY_OPTIONS,
  NAME_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  isOperatingCountry,
  normalizePhone,
  settingsFor,
  payoutMethodResponseSchema,
  payoutMethodSchema,
  responseSchema,
  updateProfileRequestSchema,
  updateProfileResponseSchema,
  type Country,
  type PayoutMethod,
  requiresKyc,
} from "@nh/shared";
import { z } from "zod";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, PasswordInput, Select } from "@/shared/ui/core";
import { useAuth } from "@/features/auth/auth.context";
import { formatOrderDate } from "@/features/admin/orders/format";

import { useFieldErrors } from "@/shared/lib/useFieldErrors";
import { AddressSection } from "./address/AddressSection";

const NO_PREFIX = { baseUrl: "" } as const;

const changedResponseSchema = responseSchema(z.object({ changed: z.literal(true) }));

/**
 * ═══════════════════════════════════════════════════════════════════
 * Mi perfil — la CredentialCard (blanca, centrada) con TRES pestañas
 * (Anahí, 27-ago): cada card de información en su propia pestaña.
 *
 *  · INFORMACIÓN — quién soy y copiar el correo.
 *  · MÉTODO DE PAGO — a dónde se pagan las comisiones.
 *  · SEGURIDAD — cambiar la contraseña, con la actual por delante.
 *
 * Compartida entre el panel de SOCIO y el de FRANQUICIATARIO.
 *
 * Los datos personales SÍ se editan desde el 1-sep. Antes solo se
 * enseñaban —"cambiarlos toca la identidad de la red y ese flujo se
 * diseña aparte"— y el resultado fue el que Anahí encontró: «¿por qué no
 * puedo editar? debo poder editar mi información». Quien tecleaba mal su
 * teléfono se quedaba con el error para siempre, y las cuentas de Google
 * —que nacen sin teléfono y con el país en "Otro"— no tenían ninguna
 * pantalla capaz de arreglarlas.
 *
 * Lo que sigue sin editarse es el CORREO, y eso sí es a propósito: es la
 * identidad con la que se entra y a donde van los enlaces de
 * recuperación. Cambiarlo necesita confirmar el nuevo por correo, y ese
 * flujo todavía no existe.
 * ═══════════════════════════════════════════════════════════════════
 */
const PERFIL_TABS = new Set(["info", "pago", "direcciones", "seguridad"]);
const TAB_PARAM = "tab";

export function PerfilPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // `?tab=pago` abre directo la pestaña: el correo de «registra tu cuenta
  // para cobrar» (2-oct-2026) lleva ahí en un clic.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const pedida = searchParams.get(TAB_PARAM);
    return pedida && PERFIL_TABS.has(pedida) ? pedida : "info";
  });

  const roleLabel = user?.franchiseeSince
    ? t("Franquiciataria")
    : user?.memberSince
      ? t("Socio")
      : t("Cliente");

  const initials = (user?.fullName ?? "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Mi perfil")}
        description={t("Tus datos, a dónde te pagamos y tu contraseña.")}
      />

      <CredentialCard
        initials={initials}
        title={user?.fullName ?? ""}
        subtitle={`${roleLabel} · ${user?.email ?? ""}`}
        tabs={[
          { key: "info", label: t("Información"), icon: <UserRound className="h-4 w-4" /> },
          { key: "pago", label: t("Método de pago"), icon: <Landmark className="h-4 w-4" /> },
          /*
            ── Direcciones, en su propia pestaña (Anahí, 29-ago) ────────
            Colgaban debajo de los datos de la cuenta, así que la
            pestaña "Información" crecía sin fondo: teléfono, país,
            fechas, copiar correo… y una lista que puede tener ocho
            direcciones con sus botones. Separadas, cada pestaña cabe en
            una pantalla y se llega a las direcciones en un clic en vez
            de haciendo scroll hasta el final.
          */
          { key: "direcciones", label: t("Direcciones"), icon: <MapPin className="h-4 w-4" /> },
          { key: "seguridad", label: t("Seguridad"), icon: <KeyRound className="h-4 w-4" /> },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "info" && <InfoSection />}

        {tab === "direcciones" && <AddressSection />}
        {tab === "pago" && <PayoutSection />}
        {tab === "seguridad" && <PasswordSection />}
      </CredentialCard>
    </>
  );
}

/**
 * A dónde se pagan las comisiones: México (banco, CLABE y titular) o
 * Estados Unidos (banco, número de cuenta, routing y titular) — Anahí,
 * 2-oct-2026: los socios que cobran allá no tienen CLABE.
 *
 * El formulario guarda los dos juegos de campos a la vez y manda solo el
 * del país elegido: cambiar de país y regresar no borra lo escrito.
 */
interface PayoutForm {
  type: "clabe" | "us_ach";
  bankName: string;
  holderName: string;
  clabe: string;
  accountNumber: string;
  routingNumber: string;
}

function toForm(method: PayoutMethod | null | undefined, fallback: PayoutForm["type"]): PayoutForm {
  const base: PayoutForm = { type: fallback, bankName: "", holderName: "", clabe: "", accountNumber: "", routingNumber: "" };
  if (!method) return base;
  return method.type === "us_ach"
    ? { ...base, ...method, type: "us_ach" }
    : { ...base, bankName: method.bankName, holderName: method.holderName, clabe: method.clabe, type: "clabe" };
}

function fromForm(form: PayoutForm): unknown {
  return form.type === "us_ach"
    ? {
        type: "us_ach",
        bankName: form.bankName,
        holderName: form.holderName,
        accountNumber: form.accountNumber,
        routingNumber: form.routingNumber,
      }
    : { type: "clabe", bankName: form.bankName, holderName: form.holderName, clabe: form.clabe };
}

function PayoutSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const payout = useQuery({
    queryKey: ["me", "payout-method"],
    queryFn: async () => {
      const data = await authFetch(AUTH_ROUTES.payoutMethod, payoutMethodResponseSchema, NO_PREFIX);
      return data.payoutMethod;
    },
  });

  const [form, setForm] = useState<PayoutForm | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (payout.data !== undefined && !form) {
      setForm(toForm(payout.data, user?.country === "US" ? "us_ach" : "clabe"));
    }
  }, [payout.data, form, user?.country]);

  const guardar = useMutation({
    mutationFn: async (input: PayoutMethod) => {
      const data = await authFetch(AUTH_ROUTES.payoutMethod, payoutMethodResponseSchema, {
        ...NO_PREFIX,
        method: "PUT",
        body: input,
      });
      return data.payoutMethod;
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(["me", "payout-method"], fresh);
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setError(null);
    setSaved(false);
    const parsed = payoutMethodSchema.safeParse(fromForm(form));
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa los datos bancarios"));
      return;
    }
    guardar.mutate(parsed.data);
  }

  if (!form) return null;
  const us = form.type === "us_ach";

  return (
    <form onSubmit={enviar} className="grid gap-4">
      <p className="text-[12.5px] text-ds-ink-3">
        {requiresKyc(user?.country)
          ? t("A esta cuenta se pagan tus comisiones. El pago requiere tu KYC aprobado.")
          : t("A esta cuenta se pagan tus comisiones.")}
      </p>
      <Select
        label={t("País de la cuenta")}
        value={form.type}
        onChange={(event) =>
          setForm({ ...form, type: event.target.value === "us_ach" ? "us_ach" : "clabe" })
        }
        options={[
          { value: "clabe", label: t("México (CLABE)") },
          { value: "us_ach", label: t("Estados Unidos (ACH)") },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t("Banco")}
          value={form.bankName}
          onChange={(event) => setForm({ ...form, bankName: event.target.value })}
          placeholder={us ? "Chase" : "BBVA"}
        />
        <Input
          label={t("Titular")}
          value={form.holderName}
          onChange={(event) => setForm({ ...form, holderName: event.target.value })}
          placeholder={t("Como aparece en el banco")}
        />
      </div>
      {us ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("Número de cuenta")}
            value={form.accountNumber}
            onChange={(event) =>
              setForm({ ...form, accountNumber: event.target.value.replace(/\D/g, "") })
            }
            maxLength={17}
            inputMode="numeric"
            placeholder="000123456789"
          />
          <Input
            label={t("Routing number (9 dígitos)")}
            value={form.routingNumber}
            onChange={(event) =>
              setForm({ ...form, routingNumber: event.target.value.replace(/\D/g, "") })
            }
            maxLength={9}
            inputMode="numeric"
            placeholder="111000025"
          />
        </div>
      ) : (
        <Input
          label={t("CLABE (18 dígitos)")}
          value={form.clabe}
          onChange={(event) => setForm({ ...form, clabe: event.target.value.replace(/\D/g, "") })}
          maxLength={18}
          inputMode="numeric"
          placeholder="012345678901234567"
        />
      )}

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert variant="success" onDismiss={() => setSaved(false)}>
          {t("Guardado. Tus comisiones se pagarán a esta cuenta.")}
        </Alert>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={guardar.isPending}>
          {t("Guardar método de pago")}
        </Button>
      </div>
    </form>
  );
}

/** Cambiar la contraseña, con la actual por delante. */
/**
 * Los datos propios, editables (Anahí, 1-sep).
 *
 * ── Por qué un formulario y no una card con un lápiz ─────────────────
 * Son cuatro campos. Un modo "ver" que hay que abrir para pasar a un modo
 * "editar" añade un clic y un estado más para no ahorrar nada: la
 * pantalla ya es la pantalla de mis datos, y quien entra a ella entra a
 * mirarlos o a corregirlos. Las fechas —socia desde, franquiciataria
 * desde— sí se quedan como texto: no son datos que nadie escriba, son
 * consecuencias de lo que la cuenta ha hecho.
 *
 * ── El correo se enseña y no se toca ─────────────────────────────────
 * Está arriba, en la cabecera de la card, y aquí abajo el botón de
 * copiarlo. Cambiarlo es cambiar la identidad con la que se entra: pide
 * confirmar el nuevo por correo, y ese flujo no existe todavía.
 */
function InfoSection() {
  const { t } = useTranslation();
  const { user, restore } = useAuth();
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState<string>("");
  const [phone, setPhone] = useState("");

  /*
   * Los errores POR CAMPO, contra el mismo esquema que valida el
   * servidor. Antes el formulario callaba hasta que se pulsaba Guardar y
   * entonces soltaba UN aviso para cuatro campos: quien había tecleado
   * veinte dígitos en el teléfono no se enteraba hasta el final, y ni
   * siquiera sabía cuál de los cuatro era el que estorbaba.
   */
  const campos = useFieldErrors(updateProfileRequestSchema);
  const valores = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    country,
    phone: phone.trim(),
  };

  /*
   * Los campos se siembran de la sesión cuando llega y cada vez que
   * cambia. El país de una cuenta de Google llega como "OTHER", que ES
   * una opción válida del selector: se enseña tal cual en vez de vaciarlo
   * —vaciarlo escondería que hay algo que corregir—.
   */
  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setCountry(user.country);
    setPhone(user.phone);
    // Lo que llega del servidor nunca es culpa de quien mira: si la
    // cuenta viene incompleta (Google), se avisa arriba, no marcando
    // en rojo campos que esa persona no ha tocado.
    campos.reiniciar();
  }, [user, campos.reiniciar]);

  const guardar = useMutation({
    mutationFn: (input: { firstName: string; lastName: string; country: Country; phone: string }) =>
      authFetch(AUTH_ROUTES.profile, updateProfileResponseSchema, {
        ...NO_PREFIX,
        method: "PUT",
        body: input,
      }),
    onSuccess: async () => {
      /*
       * La sesión se vuelve a leer del servidor en vez de escribir a mano
       * el usuario en el contexto. El nombre sale en la cabecera, en el
       * saludo del panel y en la tienda: si se actualizara solo aquí, la
       * pantalla diría una cosa y el resto de la app otra hasta la
       * siguiente recarga.
       */
      await restore();
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);

    /*
     * Al enviar se marcan TODOS los campos que estorban, cada uno con su
     * mensaje debajo, y no se gasta una petición: el servidor devolvería
     * exactamente lo mismo —es el mismo esquema— unos cientos de
     * milisegundos más tarde.
     */
    /*
     * Se normaliza también AQUÍ y no solo al salir del campo: quien
     * teclea el teléfono y pulsa Guardar directamente —con Enter, o
     * porque es el último campo— nunca dispara el `blur`, y quedarse sin
     * guardar por eso sería el mismo problema con otro disfraz.
     */
    const telefono = normalizePhone(phone, settingsFor(country).dialCode);
    if (telefono) setPhone(telefono);

    const datos = campos.validar({ ...valores, phone: telefono ?? phone.trim() });
    if (!datos) return;
    guardar.mutate(datos);
  }

  async function copiarCorreo() {
    if (!user?.email) return;
    try {
      await navigator.clipboard.writeText(user.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // El correo queda a la vista en la cabecera para copiarlo a mano.
    }
  }

  return (
    <form onSubmit={enviar} className="grid gap-4">
      {/*
        El aviso de perfil incompleto. Una cuenta de Google llega sin
        teléfono, y sin él no hay a dónde llamar cuando un pedido tiene un
        problema: se dice al entrar, no cuando ya hizo falta.
      */}
      {user && !user.phone && (
        <Alert variant="warning">
          {t("Te falta el teléfono. Es a donde te llamamos si tu pedido tiene un problema.")}
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {/*
          Cada campo lleva las TRES cosas, y las tres hacen falta:
           · `maxLength` del contrato — el tope no se puede ni teclear,
             así que no hay nada que corregir después.
           · `onBlur` — al salir, el problema aparece debajo del campo.
           · `onChange` que limpia — el error se va con la primera tecla.
        */}
        <Input
          label={t("Nombre")}
          value={firstName}
          maxLength={NAME_MAX_LENGTH}
          error={campos.errors.firstName}
          onChange={(event) => {
            setFirstName(event.target.value);
            campos.limpiar("firstName");
          }}
          onBlur={() => campos.revisar("firstName", valores)}
          autoComplete="given-name"
        />
        <Input
          label={t("Apellidos")}
          value={lastName}
          maxLength={NAME_MAX_LENGTH}
          error={campos.errors.lastName}
          onChange={(event) => {
            setLastName(event.target.value);
            campos.limpiar("lastName");
          }}
          onBlur={() => campos.revisar("lastName", valores)}
          autoComplete="family-name"
        />
        <Input
          label={t("Teléfono")}
          value={phone}
          /*
            16 = el `+` más los 15 dígitos del estándar E.164. Es el techo
            del propio esquema, importado y no escrito a mano: es lo que
            impide que la caja acepte los veinte dígitos que aceptaba.
          */
          maxLength={PHONE_MAX_LENGTH}
          error={campos.errors.phone}
          onChange={(event) => {
            setPhone(event.target.value);
            campos.limpiar("phone");
          }}
          /*
            ── Al salir, se COMPLETA en vez de regañar (Anahí, 1-sep) ──
            «No me deja guardar la información»: había escrito
            `55555555`, un número local sin lada, y el formulario se
            limitaba a decir que no era válido. El teléfono se guarda en
            E.164 —con `+` y el país— porque es lo que necesita una guía
            de envío, pero nadie escribe su propio teléfono así.

            `normalizePhone` ya existía y ya sabía hacer esto; lo que
            faltaba era llamarlo. Diez dígitos sueltos se convierten en
            `+52…` o `+1…` según el país ELEGIDO ARRIBA — otra cosa que
            el país decide. Si aun así no se entiende, entonces sí sale
            el error, y ahora con un intento de ayuda por delante.
          */
          onBlur={() => {
            const completo = normalizePhone(phone, settingsFor(country).dialCode);
            const valor = completo ?? phone.trim();
            if (completo) setPhone(completo);
            campos.revisar("phone", { ...valores, phone: valor });
          }}
          autoComplete="tel"
          inputMode="tel"
          placeholder={settingsFor(country).dialCode === "+1" ? "5551234567" : "5512345678"}
          hint={t("Tu número local. Le ponemos la lada de {{pais}}.", {
            pais: settingsFor(country).label,
          })}
        />
        {/*
          ── El país decide TODO (Anahí, 1-sep) ────────────────────────
          Idioma, moneda y cuenta de Stripe que cobra. Por eso solo salen
          los países donde HAY cuenta de Stripe: un tercer país no es una
          opción a medias, es un cobro que no se puede hacer.

          Una cuenta que venga con `OTHER` —las de Google nacen así,
          porque Google no entrega el país— cae al hueco vacío del
          `placeholder` y tiene que elegir. Enseñarle "Otro" seleccionado
          escondería que hay una decisión pendiente que afecta a su
          próxima compra.
        */}
        <Select
          label={t("País")}
          value={isOperatingCountry(country) ? country : ""}
          placeholder={t("Elige tu país")}
          error={campos.errors.country}
          hint={t("Decide tu idioma y la moneda en la que se te cobra")}
          onChange={(event) => {
            setCountry(event.target.value);
            campos.limpiar("country");
            /*
             * Al cambiar de país, el teléfono se vuelve a normalizar con
             * la lada NUEVA: quien pasa de México a Estados Unidos con
             * un número local escrito no debería quedarse con un +52 que
             * ya no le corresponde.
             */
            const conLadaNueva = normalizePhone(
              phone,
              settingsFor(event.target.value).dialCode,
            );
            if (conLadaNueva) setPhone(conLadaNueva);
          }}
          onBlur={() => campos.revisar("country", valores)}
          options={COUNTRY_OPTIONS}
        />
      </div>

      {/*
        Lo que NO se escribe: el correo con el que se entra y las fechas
        que la cuenta se ganó.
      */}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CredentialRow label={t("Correo")} value={user?.email ?? "—"} />
        {user?.memberSince && (
          <CredentialRow label={t("Socio desde")} value={formatOrderDate(user.memberSince)} />
        )}
        {user?.franchiseeSince && (
          <CredentialRow
            label={t("Franquiciataria desde")}
            value={formatOrderDate(user.franchiseeSince)}
          />
        )}
      </dl>

      {error && <Alert variant="error">{error}</Alert>}
      {saved && <Alert variant="success">{t("Guardado")}</Alert>}

      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={guardar.isPending}>
          {t("Guardar cambios")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          leftIcon={copied ? <Check /> : <Copy />}
          onClick={() => void copiarCorreo()}
        >
          {copied ? t("Copiado") : t("Copiar mi correo")}
        </Button>
      </div>
    </form>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cambiar = useMutation({
    mutationFn: () =>
      authFetch(AUTH_ROUTES.changePassword, changedResponseSchema, {
        ...NO_PREFIX,
        method: "POST",
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      setSaved(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo cambiar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (next !== confirm) {
      setError(t("Las contraseñas no coinciden"));
      return;
    }
    const parsed = changePasswordRequestSchema.safeParse({
      currentPassword: current,
      newPassword: next,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa la contraseña nueva"));
      return;
    }
    cambiar.mutate();
  }

  return (
    <form onSubmit={enviar} className="grid gap-4">
      <PasswordInput
        label={t("Contraseña actual")}
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        autoComplete="current-password"
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <PasswordInput
          label={t("Nueva contraseña")}
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
        />
        <PasswordInput
          label={t("Confírmala")}
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
        />
      </div>

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert variant="success" onDismiss={() => setSaved(false)}>
          {t("Contraseña cambiada. Úsala en tu siguiente inicio de sesión.")}
        </Alert>
      )}

      <div className="flex justify-end">
        <Button type="submit" loading={cambiar.isPending}>
          {t("Cambiar contraseña")}
        </Button>
      </div>
    </form>
  );
}
